import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const diffusionCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Diffusion',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.productDeliveryLocationDiffusion.`,
});

function buildCacheKey({ productId, deliveryLocationId, minDate, maxDate, latestDate }) {
	return `diffusions.${productId ?? 'all'}.${deliveryLocationId ?? 'all'}.${minDate ?? '_'}.${maxDate ?? '_'}.${latestDate ?? '_'}`;
}

// Deliberately a DIFFERENT key from buildCacheKey({ deliveryLocationId }) above — see
// preloadAllProductDeliveryLocationDiffusions for why the window-bounded preload must never write
// under the unbounded on-demand lookup's own key.
function buildPreloadCacheKey(deliveryLocationId) {
	return `diffusions.preload.${deliveryLocationId}`;
}

async function getPreloadedDiffusions(deliveryLocationId) {
	if (deliveryLocationId == null) return null;
	try {
		return await diffusionCache.get(buildPreloadCacheKey(deliveryLocationId));
	} catch {
		return null;
	}
}

function buildRequestUrl({ productId, deliveryLocationId, minDate, maxDate, latestDate }) {
	const params = new URLSearchParams();
	if (productId != null) params.append('productId', productId);
	if (deliveryLocationId != null) params.append('deliveryLocationId', deliveryLocationId);
	if (minDate) params.append('minDate', minDate);
	if (maxDate) params.append('maxDate', maxDate);
	if (latestDate != null) params.append('latestDate', latestDate);

	const query = params.toString();
	return getApiUrl('Assort/ProductDeliveryLocationDiffusions') + (query ? `?${query}` : '');
}

/**
 * Get sell-out (datasharing) diffusions for a product / delivery location.
 * Each row holds: date, productId, ean, productReference, productLabel, volume, value, vmm, vmh.
 * Offline-first: served from memory cache, then IndexedDB, then API (with background refresh).
 */
export async function getProductDeliveryLocationDiffusions(filters = {}, forceRefresh = false) {
	const cacheKey = buildCacheKey(filters);
	return diffusionCache.fetchOrCache(cacheKey, () => fetchDiffusionsFromAPI(filters), {
		forceRefresh,
		fallback: async () => (await getPreloadedDiffusions(filters.deliveryLocationId)) ?? [],
		backgroundRefresh: true,
	});
}

async function requestDiffusions(filters) {
	const response = await authenticatedFetch(buildRequestUrl(filters), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	return (await response.json()) || [];
}

async function fetchDiffusionsFromAPI(filters) {
	const diffusions = await requestDiffusions(filters);
	log('success', 'Diffusion', `✅ Fetched ${diffusions.length} product delivery location diffusions`);
	return diffusions;
}

export async function refreshProductDeliveryLocationDiffusions() {
	diffusionCache.clearMemory();
	await diffusionCache.clear();
}

const SELLOUT_OFFLINE_WINDOW_MONTHS = 6;

function sellOutPreloadMinDate() {
	const date = new Date();
	date.setMonth(date.getMonth() - SELLOUT_OFFLINE_WINDOW_MONTHS);
	return date.toISOString();
}

/**
 * Preload sell-out diffusions for offline use during the Vue background sync, so the DataSharing
 * tab (RG #23004 §7) has data available offline even if the user never opened it while online.
 *
 * One bulk request (no deliveryLocationId filter) instead of one request per store, bounded to the
 * last 6 months (iOS Jetsam — tasks/lessons.md #48-52). The endpoint returns the whole territory,
 * so we bucket the response by row.deliveryLocationId and cache only the rep's own stores
 * (deliveryLocationIds) to keep the persisted footprint bounded. Cached under its OWN key
 * (buildPreloadCacheKey), never the unbounded on-demand lookup's key: a product whose only sell-out
 * history falls outside this 6-month window would otherwise get its unbounded cache entry silently
 * shadowed by an empty/partial preload bucket — getProductDeliveryLocationDiffusions treats any
 * existing cache entry as "good enough" and serves it as-is (only a delayed, fire-and-forget
 * background refresh follows), so the first online on-demand lookup for that store would render
 * stale/empty diffusions instead of waiting for the real fetch. getProductDeliveryLocationDiffusions
 * only reads this preload key as an offline-only fallback, once the real (unbounded) cache is empty.
 */
export async function preloadAllProductDeliveryLocationDiffusions(deliveryLocationIds = []) {
	if (!iframeDataBridge.isOnline()) return;
	if (!Array.isArray(deliveryLocationIds) || deliveryLocationIds.length === 0) return;

	try {
		const rows = await requestDiffusions({ minDate: sellOutPreloadMinDate() });
		const byDeliveryLocation = new Map(deliveryLocationIds.map((id) => [id, []]));
		for (const row of rows) {
			const bucket = byDeliveryLocation.get(row?.deliveryLocationId);
			if (bucket) bucket.push(row);
		}

		for (const [deliveryLocationId, diffusions] of byDeliveryLocation) {
			await diffusionCache.put(buildPreloadCacheKey(deliveryLocationId), diffusions);
		}

		log('success', 'Diffusion', `✅ Preloaded sell-out diffusions for ${byDeliveryLocation.size} delivery locations in one bulk request`);
	} catch (error) {
		log('warn', 'Diffusion', `⚠️ Failed to bulk-preload sell-out diffusions: ${error.message}`);
	}
}
